/**
 * SQLite offline store: uses db-bridge (wa-sqlite + OPFS).
 */

import { db } from '../../orm/connection';
import {
  ensureSyncSchemaReady,
  runEntityMigrations,
} from '../../orm/sync-schema';
import { schemaRegistry } from '../../orm/schema-registry';
import {
  LocalOrderSchema,
  LocalOrderItemSchema,
  LocalOrderItemSaleSchema,
  CustomerSyncStatusSchema,
  CustomerOrderItemCacheSchema,
} from '../../orm/entities';
import type {
  SyncHistoryRow,
  LocalOrderRow,
  LocalOrderItemRow,
  LocalOrderItemSaleRow,
  LocalOrderWithItems,
  SyncQueueEntry,
  PendingConflictRow,
  DbSizeRow,
  EntityBreakdownRow,
} from './sqlite-store-types';

export interface SyncStateRow {
  phase: number;
  entity_type: string;
  last_cursor: string | null;
  last_synced_at: string | null;
  record_count: number;
}

export interface NdjsonRecord {
  type: string;
  id: string;
  data: Record<string, unknown>;
}

const ENTITIES_TABLE = 'offline_entities';
const SYNC_STATE_TABLE = 'sync_state';
const SYNC_HISTORY_TABLE = 'sync_history';
const SYNCED_CUSTOMER_CODES_TABLE = 'synced_customer_codes';
const PENDING_CONFLICTS_TABLE = 'pending_conflicts';
const LOCAL_ORDERS_TABLE = 'local_orders';
const LOCAL_ORDER_ITEMS_TABLE = 'local_order_items';
const LOCAL_ORDER_ITEM_SALES_TABLE = 'local_order_item_sales';
const SYNC_QUEUE_TABLE = 'sync_queue';
const PHASE1_FRESH_HOURS = 24;
let entitySchemasRegistered = false;
let entityTablesReadyPromise: Promise<void> | null = null;
let sqliteStoreInitPromise: Promise<void> | null = null;

function registerEntitySchemasOnce(): void {
  if (entitySchemasRegistered) return;
  schemaRegistry.register(LocalOrderSchema);
  schemaRegistry.register(LocalOrderItemSchema);
  schemaRegistry.register(LocalOrderItemSaleSchema);
  schemaRegistry.register(CustomerSyncStatusSchema);
  schemaRegistry.register(CustomerOrderItemCacheSchema);
  entitySchemasRegistered = true;
}

async function ensureEntityTablesReady(): Promise<void> {
  if (entityTablesReadyPromise) {
    await entityTablesReadyPromise;
    return;
  }

  const promise = (async () => {
    registerEntitySchemasOnce();
    await schemaRegistry.synchronize();
    await runEntityMigrations();
  })();

  entityTablesReadyPromise = promise;
  try {
    await promise;
  } finally {
    if (entityTablesReadyPromise === promise) {
      entityTablesReadyPromise = null;
    }
  }
}

export async function initSqliteStore(): Promise<void> {
  if (sqliteStoreInitPromise) {
    await sqliteStoreInitPromise;
    return;
  }

  const promise = (async () => {
    if (!db.isInitialized) {
      await db.initialize({ database: 'b2b_offline' });
    }
    // Always ensure sync DDL — WorkerHealthGate may have initialized the connection
    // without running createSyncTables (e.g. before login).
    await ensureSyncSchemaReady();
    await ensureEntityTablesReady();
  })();

  sqliteStoreInitPromise = promise;
  try {
    await promise;
  } finally {
    if (sqliteStoreInitPromise === promise) {
      sqliteStoreInitPromise = null;
    }
  }
}

export async function persistToIndexedDB(): Promise<void> {
  // OPFS persists automatically; no-op
}

export type FlushBatchEntityInsertMode = 'upsert' | 'plain';

export interface FlushBatchOptions {
  entityInsertMode?: FlushBatchEntityInsertMode;
}

async function _flushBatch(
  records: NdjsonRecord[],
  phase: number,
  entityType: string,
  lastCursor: string,
  recordCountDelta: number,
  options?: FlushBatchOptions,
): Promise<{ recordCount: number }> {
  const type = String(entityType ?? '').trim() || 'unknown';
  const phaseNum = phase === 1 || phase === 2 ? phase : 1;
  const insertMode = options?.entityInsertMode === 'plain' ? 'plain' : 'upsert';

  const rows: Array<{ entityId: string; dataJson: string }> = [];
  const syncedKeys: string[] = [];

  for (const r of records) {
    const rawId = (r?.id ?? '').toString().trim();
    if (!rawId) continue;
    const table = (r?.data as { _table?: string })?._table;
    const entityId = type === 'article' && table ? `${table}:${rawId}` : rawId;
    rows.push({
      entityId,
      dataJson: JSON.stringify(r?.data ?? {}),
    });
    if (type === 'agent_customer_item' && r?.data) {
      const d = r.data as {
        commercialCode?: string;
        commercial_code?: string;
        locationCode?: string;
        location_code?: string;
      };
      const code = (d.commercialCode ?? d.commercial_code ?? '')
        .toString()
        .trim();
      const loc = (d.locationCode ?? d.location_code ?? '').toString().trim();
      const key = (code + loc).toUpperCase().replace(/\s/g, '');
      if (key) {
        syncedKeys.push(key);
      }
    }
  }

  const cursorTrim = String(lastCursor ?? '').trim();

  await db.bulkUpsertOfflineEntities({
    entityType: type,
    phase: phaseNum,
    rows,
    syncedCustomerKeys: syncedKeys.length > 0 ? syncedKeys : undefined,
    recordCountDelta: recordCountDelta ?? 0,
    cursorWatermark: cursorTrim.length > 0 ? cursorTrim : null,
    entityInsertMode: insertMode,
  });

  // Evita round-trip getSyncState sul path caldo: i chiamanti usano i conteggi orchestrator.
  return { recordCount: 0 };
}

/**
 * High-watermark cursor update: only advances the cursor forward (lexicographic compare).
 * Usato da flush incrementali fuori dal path bulk; `flushBatch` include già il cursore nel bulk worker.
 */
export async function advanceCursorHighWatermark(
  phase: number,
  entityType: string,
  cursor: string,
): Promise<void> {
  if (!cursor) return;
  const phaseNum = phase === 1 || phase === 2 ? phase : 1;
  const type = String(entityType ?? '').trim() || 'unknown';
  await db.run(
    `UPDATE ${SYNC_STATE_TABLE} SET last_cursor = ? WHERE phase = ? AND entity_type = ? AND (last_cursor IS NULL OR last_cursor = '' OR last_cursor < ?)`,
    [cursor, phaseNum, type, cursor],
  );
}

export async function flushBatch(
  records: NdjsonRecord[],
  phase: number,
  entityType: string,
  lastCursor: string,
  recordCountDelta: number,
  options?: FlushBatchOptions,
): Promise<{ recordCount: number }> {
  return _flushBatch(
    records,
    phase,
    entityType,
    lastCursor,
    recordCountDelta,
    options,
  );
}

/**
 * Mark an entity type as synced without inserting any records.
 * Used after a successful Phase stream to ensure sync_state entries exist
 * for types that received 0 incremental records (e.g. agent with cursor past last id).
 */
export async function touchSyncState(
  phase: number,
  entityType: string,
): Promise<void> {
  const phaseNum = phase === 1 || phase === 2 ? phase : 1;
  const type = String(entityType ?? '').trim() || 'unknown';
  await db.run(
    `INSERT INTO ${SYNC_STATE_TABLE} (phase, entity_type, last_cursor, last_synced_at, record_count)
      VALUES (?, ?, '', ?, 0)
      ON CONFLICT(phase, entity_type) DO UPDATE SET
        last_synced_at = excluded.last_synced_at`,
    [phaseNum, type, new Date().toISOString()],
  );
}

/** Un solo round-trip worker per aggiornare last_synced_at su più entity_type. */
export async function touchSyncStateBatch(
  phase: number,
  entityTypes: readonly string[],
): Promise<void> {
  if (entityTypes.length === 0) return;
  const phaseNum = phase === 1 || phase === 2 ? phase : 1;
  const now = new Date().toISOString();
  const statements = entityTypes.map((raw) => {
    const type = String(raw ?? '').trim() || 'unknown';
    return {
      statement: `INSERT INTO ${SYNC_STATE_TABLE} (phase, entity_type, last_cursor, last_synced_at, record_count)
      VALUES (?, ?, '', ?, 0)
      ON CONFLICT(phase, entity_type) DO UPDATE SET
        last_synced_at = excluded.last_synced_at`,
      values: [phaseNum, type, now],
    };
  });
  await db.executeSet(statements);
}

export async function getSyncState(
  phase: number,
  entityType: string,
): Promise<SyncStateRow | null> {
  const rows = await db.query<SyncStateRow>(
    `SELECT phase, entity_type, last_cursor, last_synced_at, record_count FROM ${SYNC_STATE_TABLE} WHERE phase = ? AND entity_type = ?`,
    [phase, entityType],
  );
  if (!rows.length) return null;
  const r = rows[0];
  const rc = r.record_count;
  const recordCount =
    typeof rc === 'number' && !Number.isNaN(rc)
      ? rc
      : Math.max(0, Number(rc) || 0);
  return { ...r, record_count: recordCount };
}

export async function getEntitiesByType(
  entityType: string,
): Promise<Array<{ id: string; data: Record<string, unknown> }>> {
  const rows = await db.query<{ entity_id: string; data: string }>(
    `SELECT entity_id, data FROM ${ENTITIES_TABLE} WHERE entity_type = ?`,
    [entityType],
  );
  return rows.map((r) => {
    let data: Record<string, unknown> = {};
    try {
      data = JSON.parse(r.data) as Record<string, unknown>;
    } catch {
      /* ignore */
    }
    return { id: r.entity_id, data };
  });
}

const COMMERCIAL_CODE_IN_CHUNK = 100;

/**
 * Codici cliente (campo visita) distinti per la data indicata (YYYY-MM-DD), senza materializzare tutte le visite.
 */
export async function getVisitCustomerCommercialCodesForDate(
  isoDate: string,
): Promise<string[]> {
  const day = String(isoDate ?? '')
    .trim()
    .slice(0, 10);
  if (!day) return [];
  const rows = await db.query<{ code: string | null }>(
    `SELECT DISTINCT trim(json_extract(data, '$.customerCommercialCode')) AS code
     FROM ${ENTITIES_TABLE}
     WHERE entity_type = 'visit'
       AND substr(COALESCE(json_extract(data, '$.visitDate'), ''), 1, 10) = ?
       AND json_extract(data, '$.customerCommercialCode') IS NOT NULL
       AND length(trim(json_extract(data, '$.customerCommercialCode'))) > 0`,
    [day],
  );
  const out: string[] = [];
  const seen = new Set<string>();
  for (const r of rows) {
    const c = (r.code ?? '').toString().trim();
    if (!c || seen.has(c)) continue;
    seen.add(c);
    out.push(c);
  }
  return out;
}

/**
 * commercialCode + locationCode da tabella customer per un sottoinsieme di codici (evita full scan clienti).
 */
export async function getCustomerLocationPairsForCommercialCodes(
  commercialCodes: string[],
): Promise<Array<{ commercialCode: string; locationCode: string | null }>> {
  const codes = Array.from(
    new Set(commercialCodes.map((c) => String(c).trim()).filter(Boolean)),
  );
  if (codes.length === 0) return [];

  const result: Array<{ commercialCode: string; locationCode: string | null }> =
    [];
  for (let i = 0; i < codes.length; i += COMMERCIAL_CODE_IN_CHUNK) {
    const slice = codes.slice(i, i + COMMERCIAL_CODE_IN_CHUNK);
    const placeholders = slice.map(() => '?').join(', ');
    const rows = await db.query<{ cc: string | null; lc: string | null }>(
      `SELECT json_extract(data, '$.commercialCode') AS cc,
              json_extract(data, '$.locationCode') AS lc
       FROM ${ENTITIES_TABLE}
       WHERE entity_type = 'customer'
         AND trim(json_extract(data, '$.commercialCode')) IN (${placeholders})`,
      slice,
    );
    for (const r of rows) {
      const cc = (r.cc ?? '').toString().trim();
      if (!cc) continue;
      const lcRaw = (r.lc ?? '').toString().trim();
      result.push({
        commercialCode: cc,
        locationCode: lcRaw.length > 0 ? lcRaw : null,
      });
    }
  }
  return result;
}

export async function getPhase1LastSyncedAt(): Promise<string | null> {
  const rows = await db.query<{ max: string | null }>(
    `SELECT MAX(last_synced_at) as max FROM ${SYNC_STATE_TABLE} WHERE phase = 1`,
  );
  const val = rows[0]?.max;
  return typeof val === 'string' ? val : null;
}

export async function getPhase2LastSyncedAt(): Promise<string | null> {
  const rows = await db.query<{ max: string | null }>(
    `SELECT MAX(last_synced_at) as max FROM ${SYNC_STATE_TABLE} WHERE phase = 2`,
  );
  const val = rows[0]?.max;
  return typeof val === 'string' ? val : null;
}

export async function getAllSyncStates(): Promise<SyncStateRow[]> {
  const rows = await db.query<SyncStateRow>(
    `SELECT phase, entity_type, last_cursor, last_synced_at, record_count FROM ${SYNC_STATE_TABLE}`,
  );
  return rows.map((r) => ({
    ...r,
    record_count:
      typeof r.record_count === 'number'
        ? r.record_count
        : Math.max(0, Number(r.record_count) || 0),
  }));
}

const PHASE1_REQUIRED_ENTITY_TYPES = ['article', 'customer', 'agent'] as const;

export async function isPhase1Complete(): Promise<boolean> {
  const lastAt = await getPhase1LastSyncedAt();
  if (!lastAt) return false;
  const rows = await db.query<{ entity_type: string }>(
    `SELECT entity_type FROM ${SYNC_STATE_TABLE} WHERE phase = 1 AND last_synced_at IS NOT NULL`,
  );
  const synced = new Set(rows.map((r) => r.entity_type));
  return PHASE1_REQUIRED_ENTITY_TYPES.every((t) => synced.has(t));
}

export async function shouldSkipPhase1(): Promise<boolean> {
  if (!(await isPhase1Complete())) return false;
  const lastAt = await getPhase1LastSyncedAt();
  if (!lastAt) return false;
  const last = new Date(lastAt).getTime();
  const now = Date.now();
  return now - last < PHASE1_FRESH_HOURS * 60 * 60 * 1000;
}

export async function shouldSkipFullSync(): Promise<boolean> {
  if (!(await isPhase1Complete())) return false;
  const lastAt = await getPhase2LastSyncedAt();
  if (typeof lastAt !== 'string' || !lastAt) return false;
  const last = new Date(lastAt).getTime();
  const now = Date.now();
  return now - last < PHASE1_FRESH_HOURS * 60 * 60 * 1000;
}

/** iOS (sync ridotta): salta se la fase 2 è stata eseguita di recente; il catalogo non è in SQLite. */
export async function shouldSkipIOSMinimalSync(): Promise<boolean> {
  const lastAt = await getPhase2LastSyncedAt();
  if (typeof lastAt !== 'string' || !lastAt) return false;
  const last = new Date(lastAt).getTime();
  const now = Date.now();
  return now - last < PHASE1_FRESH_HOURS * 60 * 60 * 1000;
}

export async function clearSyncState(): Promise<void> {
  await db.execute(`DELETE FROM ${SYNC_STATE_TABLE}`);
  await db.execute(`DELETE FROM ${ENTITIES_TABLE}`);
}

export async function clearPhase2State(): Promise<void> {
  await db.execute(`DELETE FROM ${SYNC_STATE_TABLE} WHERE phase = 2`);
  await db.execute(
    `DELETE FROM ${ENTITIES_TABLE} WHERE entity_type IN ('visit', 'agent_customer_item', 'order')`,
  );
}

export async function deleteCustomerTimetableByCommercialCode(
  commercialCode: string,
): Promise<void> {
  const code = String(commercialCode).trim();
  if (!code) return;
  const items = await getEntitiesByType('customer_timetable');
  for (const item of items) {
    const d = item.data as { commercialCode?: string };
    if ((d.commercialCode ?? '').toString().trim() !== code) continue;
    await db.run(
      `DELETE FROM ${ENTITIES_TABLE} WHERE entity_type = 'customer_timetable' AND entity_id = ?`,
      [item.id],
    );
  }
}

export async function deleteAgentCustomerItemsByCommercialCode(
  commercialCode: string,
  locationCode?: string,
): Promise<void> {
  const code = String(commercialCode).trim();
  if (!code) return;
  const locFilter =
    locationCode != null && String(locationCode).trim() !== ''
      ? String(locationCode).trim()
      : undefined;
  const items = await getEntitiesByType('agent_customer_item');
  const idsToDelete: string[] = [];
  const keysToRemove = new Set<string>();
  for (const item of items) {
    const d = item.data as {
      commercialCode?: string;
      commercial_code?: string;
      locationCode?: string;
      location_code?: string;
    };
    const c = (d.commercialCode ?? d.commercial_code ?? '').toString().trim();
    if (c !== code) continue;
    const loc = (d.locationCode ?? d.location_code ?? '').toString().trim();
    if (locFilter != null && loc !== locFilter) continue;
    idsToDelete.push(item.id);
    const key = (c + loc).toUpperCase().replace(/\s/g, '');
    if (key) keysToRemove.add(key);
  }
  for (const eid of idsToDelete) {
    await db.run(
      `DELETE FROM ${ENTITIES_TABLE} WHERE entity_type = 'agent_customer_item' AND entity_id = ?`,
      [eid],
    );
  }
  for (const k of Array.from(keysToRemove)) {
    await db.run(
      `DELETE FROM ${SYNCED_CUSTOMER_CODES_TABLE} WHERE commercial_code = ?`,
      [k],
    );
  }
}

export async function insertSyncHistory(
  startedAt: string,
  finishedAt: string,
  status: 'success' | 'error',
  recordCounts: Record<string, number>,
  errorMessage?: string | null,
): Promise<void> {
  await db.run(
    `INSERT INTO ${SYNC_HISTORY_TABLE} (started_at, finished_at, status, record_counts, error_message) VALUES (?, ?, ?, ?, ?)`,
    [
      startedAt,
      finishedAt,
      status,
      JSON.stringify(recordCounts),
      errorMessage ?? null,
    ],
  );
}

export async function getSyncHistory(limit = 20): Promise<SyncHistoryRow[]> {
  const rows = await db.query<SyncHistoryRow & { record_counts: string }>(
    `SELECT id, started_at, finished_at, status, record_counts, error_message FROM ${SYNC_HISTORY_TABLE} ORDER BY id DESC LIMIT ?`,
    [Math.max(1, Math.min(limit, 100))],
  );
  return rows.map((r) => {
    let record_counts: Record<string, number> = {};
    try {
      if (typeof r.record_counts === 'string') {
        record_counts = JSON.parse(r.record_counts) as Record<string, number>;
      }
    } catch {
      /* ignore */
    }
    return { ...r, record_counts };
  });
}

export async function getSyncedCustomerCodesFromStore(): Promise<string[]> {
  const rows = await db.query<{ commercial_code: string }>(
    `SELECT commercial_code FROM ${SYNCED_CUSTOMER_CODES_TABLE}`,
  );
  return rows
    .map((r) => String(r.commercial_code ?? '').trim())
    .filter((s) => s.length > 0);
}

export async function addSyncedCustomerCodeToStore(
  code: string,
): Promise<void> {
  const key = code.toUpperCase().replace(/\s/g, '');
  if (!key) return;
  await db.run(
    `INSERT OR IGNORE INTO ${SYNCED_CUSTOMER_CODES_TABLE} (commercial_code) VALUES (?)`,
    [key],
  );
}

export type {
  SyncHistoryRow,
  LocalOrderRow,
  LocalOrderItemRow,
  LocalOrderItemSaleRow,
  LocalOrderWithItems,
  SyncQueueEntry,
  PendingConflictRow,
  DbSizeRow,
  EntityBreakdownRow,
};

export async function updateSyncState(
  phase: number,
  entityType: string,
  lastSyncAt: string,
  recordCount?: number,
): Promise<void> {
  if (recordCount !== undefined) {
    await db.run(
      `UPDATE ${SYNC_STATE_TABLE} SET last_synced_at = ?, record_count = ? WHERE phase = ? AND entity_type = ?`,
      [lastSyncAt, recordCount, phase, entityType],
    );
  } else {
    await db.run(
      `UPDATE ${SYNC_STATE_TABLE} SET last_synced_at = ? WHERE phase = ? AND entity_type = ?`,
      [lastSyncAt, phase, entityType],
    );
  }
}

export async function resetSyncCursor(
  phase: number,
  entityType: string,
): Promise<void> {
  await db.run(
    `UPDATE ${SYNC_STATE_TABLE} SET last_cursor = NULL WHERE phase = ? AND entity_type = ?`,
    [phase, entityType],
  );
}

export async function resetAllCursors(): Promise<void> {
  await db.execute(`UPDATE ${SYNC_STATE_TABLE} SET last_cursor = NULL`);
}

export async function conflictInsert(
  entityType: string,
  entityId: string,
  clientData: string,
  serverData: string,
): Promise<void> {
  const now = new Date().toISOString();
  await db.run(
    `INSERT INTO ${PENDING_CONFLICTS_TABLE} (entity_type, entity_id, client_data, server_data, created_at)
     VALUES (?, ?, ?, ?, ?)
     ON CONFLICT(entity_type, entity_id) DO UPDATE SET client_data = excluded.client_data, server_data = excluded.server_data, resolution = NULL, resolved_at = NULL`,
    [entityType, entityId, clientData, serverData, now],
  );
}

export async function conflictGetUnresolved(): Promise<PendingConflictRow[]> {
  const rows = await db.query<Record<string, unknown>>(
    `SELECT id, entity_type, entity_id, client_data, server_data, resolution, resolved_at, created_at
     FROM ${PENDING_CONFLICTS_TABLE} WHERE resolution IS NULL ORDER BY created_at ASC`,
  );
  return rows.map((r) => ({
    id: Number(r.id),
    entity_type: String(r.entity_type),
    entity_id: String(r.entity_id),
    client_data: String(r.client_data),
    server_data: String(r.server_data),
    resolution: r.resolution != null ? String(r.resolution) : null,
    resolved_at: r.resolved_at != null ? String(r.resolved_at) : null,
    created_at: String(r.created_at),
  }));
}

export async function conflictResolve(
  id: number,
  resolution: string,
): Promise<void> {
  const now = new Date().toISOString();
  await db.run(
    `UPDATE ${PENDING_CONFLICTS_TABLE} SET resolution = ?, resolved_at = ? WHERE id = ?`,
    [resolution, now, id],
  );
}

export async function getDatabaseSize(): Promise<DbSizeRow> {
  try {
    const pageCountRows =
      await db.query<Record<string, unknown>>('PRAGMA page_count');
    const pageSizeRows =
      await db.query<Record<string, unknown>>('PRAGMA page_size');
    const pc = pageCountRows[0]
      ? Number(Object.values(pageCountRows[0])[0]) || 0
      : 0;
    const ps = pageSizeRows[0]
      ? Number(Object.values(pageSizeRows[0])[0]) || 4096
      : 4096;
    return { sizeBytes: pc * ps };
  } catch {
    return { sizeBytes: 0 };
  }
}

export async function getEntityBreakdown(): Promise<EntityBreakdownRow[]> {
  try {
    const rows = await db.query<{
      entity_type: string;
      count: number;
      size_bytes: number;
    }>(
      `SELECT entity_type, COUNT(*) as count, COALESCE(SUM(LENGTH(data)), 0) as size_bytes FROM ${ENTITIES_TABLE} GROUP BY entity_type`,
    );
    return rows.map((r) => ({
      entity_type: String(r.entity_type),
      count: Number(r.count),
      size_bytes: Number(r.size_bytes ?? 0),
    }));
  } catch {
    return [];
  }
}

export async function deleteEntitiesByType(entityType: string): Promise<void> {
  await db.run(`DELETE FROM ${ENTITIES_TABLE} WHERE entity_type = ?`, [
    entityType,
  ]);
}

export async function clearCustomerPricingCache(): Promise<void> {
  await db.execute('DELETE FROM customer_order_items_cache');
  await db.execute('DELETE FROM customer_sync_status');
  await db.execute(`DELETE FROM ${SYNCED_CUSTOMER_CODES_TABLE}`);
}

export async function localOrderUpsert(row: LocalOrderRow): Promise<void> {
  await db.run(
    `INSERT INTO ${LOCAL_ORDERS_TABLE} (id, company_id, agent_code, customer_id, customer_code, customer_name, delivery_date, delivery_time, state, notes, draft_code, sync_error, server_id, created_at, updated_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
     ON CONFLICT(id) DO UPDATE SET company_id=excluded.company_id, agent_code=excluded.agent_code, customer_id=excluded.customer_id, customer_code=excluded.customer_code, customer_name=excluded.customer_name, delivery_date=excluded.delivery_date, delivery_time=excluded.delivery_time, state=excluded.state, notes=excluded.notes, draft_code=excluded.draft_code, sync_error=excluded.sync_error, server_id=excluded.server_id, updated_at=excluded.updated_at`,
    [
      row.id,
      row.company_id,
      row.agent_code,
      row.customer_id ?? null,
      row.customer_code,
      row.customer_name,
      row.delivery_date,
      row.delivery_time ?? null,
      row.state,
      row.notes ?? null,
      row.draft_code ?? null,
      row.sync_error ?? null,
      row.server_id ?? null,
      row.created_at,
      row.updated_at,
    ],
  );
}

async function _localOrderGet(id: string): Promise<LocalOrderWithItems | null> {
  const orderRows = await db.query<Record<string, unknown>>(
    `SELECT id, company_id, agent_code, customer_id, customer_code, customer_name, delivery_date, delivery_time, state, notes, draft_code, sync_error, server_id, created_at, updated_at FROM ${LOCAL_ORDERS_TABLE} WHERE id = ?`,
    [id],
  );
  if (!orderRows.length) return null;
  const o = orderRows[0];
  const order: LocalOrderRow = {
    id: String(o.id),
    company_id: String(o.company_id),
    agent_code: String(o.agent_code),
    customer_id: o.customer_id != null ? String(o.customer_id) : null,
    customer_code: String(o.customer_code),
    customer_name: String(o.customer_name),
    delivery_date: String(o.delivery_date),
    delivery_time: o.delivery_time != null ? String(o.delivery_time) : null,
    state: String(o.state),
    notes: o.notes != null ? String(o.notes) : null,
    draft_code: o.draft_code != null ? String(o.draft_code) : null,
    sync_error: o.sync_error != null ? String(o.sync_error) : null,
    server_id: o.server_id != null ? String(o.server_id) : null,
    created_at: String(o.created_at),
    updated_at: String(o.updated_at),
  };
  const itemRows = await db.query<Record<string, unknown>>(
    `SELECT id, local_order_id, item_id, item_code, default_price, selling_price, unit_default_price, unit_selling_price, quantity, selling_type, notes FROM ${LOCAL_ORDER_ITEMS_TABLE} WHERE local_order_id = ?`,
    [id],
  );
  const items: Array<LocalOrderItemRow & { sales: LocalOrderItemSaleRow[] }> =
    [];
  for (const ir of itemRows) {
    const itemId = String(ir.id);
    const item: LocalOrderItemRow & { sales: LocalOrderItemSaleRow[] } = {
      id: itemId,
      local_order_id: String(ir.local_order_id),
      item_id: ir.item_id != null ? String(ir.item_id) : null,
      item_code: String(ir.item_code),
      default_price: Number(ir.default_price),
      selling_price: Number(ir.selling_price),
      unit_default_price: Number(ir.unit_default_price),
      unit_selling_price: Number(ir.unit_selling_price),
      quantity: Number(ir.quantity),
      selling_type: String(ir.selling_type),
      notes: ir.notes != null ? String(ir.notes) : null,
      sales: [],
    };
    const salesRows = await db.query<Record<string, unknown>>(
      `SELECT id, local_order_item_id, sale_type_id, name, percent_value, value FROM ${LOCAL_ORDER_ITEM_SALES_TABLE} WHERE local_order_item_id = ?`,
      [itemId],
    );
    for (const sr of salesRows) {
      item.sales.push({
        id: String(sr.id),
        local_order_item_id: String(sr.local_order_item_id),
        sale_type_id: sr.sale_type_id != null ? String(sr.sale_type_id) : null,
        name: String(sr.name),
        percent_value: Number(sr.percent_value),
        value: Number(sr.value),
      });
    }
    items.push(item);
  }
  return { order, items };
}

export async function localOrderGet(
  id: string,
): Promise<LocalOrderWithItems | null> {
  return _localOrderGet(id);
}

export async function localOrderGetAll(filters: {
  companyId: string;
  agentCode: string;
  customerCode?: string;
  state?: string;
}): Promise<LocalOrderWithItems[]> {
  let sql = `SELECT id FROM ${LOCAL_ORDERS_TABLE} WHERE company_id = ? AND agent_code = ?`;
  const params: unknown[] = [filters.companyId, filters.agentCode];
  if (filters.customerCode) {
    sql += ' AND customer_code = ?';
    params.push(filters.customerCode);
  }
  if (filters.state) {
    sql += ' AND state = ?';
    params.push(filters.state);
  }
  sql += ' ORDER BY updated_at DESC';
  const idRows = await db.query<{ id: string }>(sql, params);
  const results: LocalOrderWithItems[] = [];
  for (const row of idRows) {
    const o = await _localOrderGet(row.id);
    if (o) results.push(o);
  }
  return results;
}

export async function localOrderDelete(id: string): Promise<void> {
  await db.run(`DELETE FROM ${LOCAL_ORDERS_TABLE} WHERE id = ?`, [id]);
}

export async function localOrderUpdateState(
  id: string,
  state: string,
  syncError?: string | null,
  serverId?: string | null,
): Promise<void> {
  const now = new Date().toISOString();
  if (serverId !== undefined) {
    await db.run(
      `UPDATE ${LOCAL_ORDERS_TABLE} SET state = ?, sync_error = ?, server_id = ?, updated_at = ? WHERE id = ?`,
      [state, syncError ?? null, serverId ?? null, now, id],
    );
  } else {
    await db.run(
      `UPDATE ${LOCAL_ORDERS_TABLE} SET state = ?, sync_error = ?, updated_at = ? WHERE id = ?`,
      [state, syncError ?? null, now, id],
    );
  }
}

export async function localOrderItemUpsert(
  row: LocalOrderItemRow,
): Promise<void> {
  const now = new Date().toISOString();
  await db.run(
    `INSERT INTO ${LOCAL_ORDER_ITEMS_TABLE} (id, local_order_id, item_id, item_code, default_price, selling_price, unit_default_price, unit_selling_price, quantity, selling_type, notes)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
     ON CONFLICT(id) DO UPDATE SET default_price=excluded.default_price, selling_price=excluded.selling_price, unit_default_price=excluded.unit_default_price, unit_selling_price=excluded.unit_selling_price, quantity=excluded.quantity, notes=excluded.notes`,
    [
      row.id,
      row.local_order_id,
      row.item_id ?? null,
      row.item_code,
      row.default_price,
      row.selling_price,
      row.unit_default_price,
      row.unit_selling_price,
      row.quantity,
      row.selling_type,
      row.notes ?? null,
    ],
  );
  await db.run(`UPDATE ${LOCAL_ORDERS_TABLE} SET updated_at = ? WHERE id = ?`, [
    now,
    row.local_order_id,
  ]);
}

export async function localOrderItemDelete(
  localOrderId: string,
  itemCode: string,
  sellingType: string,
): Promise<void> {
  const now = new Date().toISOString();
  await db.run(
    `DELETE FROM ${LOCAL_ORDER_ITEMS_TABLE} WHERE local_order_id = ? AND item_code = ? AND selling_type = ?`,
    [localOrderId, itemCode, sellingType],
  );
  await db.run(`UPDATE ${LOCAL_ORDERS_TABLE} SET updated_at = ? WHERE id = ?`, [
    now,
    localOrderId,
  ]);
}

export async function localOrderItemSalesReplace(
  localOrderItemId: string,
  sales: LocalOrderItemSaleRow[],
): Promise<void> {
  const orderRows = await db.query<{ local_order_id: string }>(
    `SELECT local_order_id FROM ${LOCAL_ORDER_ITEMS_TABLE} WHERE id = ?`,
    [localOrderItemId],
  );
  const orderId = orderRows[0]?.local_order_id;
  const now = new Date().toISOString();
  await db.run(
    `DELETE FROM ${LOCAL_ORDER_ITEM_SALES_TABLE} WHERE local_order_item_id = ?`,
    [localOrderItemId],
  );
  for (const s of sales) {
    await db.run(
      `INSERT INTO ${LOCAL_ORDER_ITEM_SALES_TABLE} (id, local_order_item_id, sale_type_id, name, percent_value, value) VALUES (?, ?, ?, ?, ?, ?)`,
      [
        s.id,
        s.local_order_item_id,
        s.sale_type_id ?? null,
        s.name,
        s.percent_value,
        s.value,
      ],
    );
  }
  if (orderId) {
    await db.run(
      `UPDATE ${LOCAL_ORDERS_TABLE} SET updated_at = ? WHERE id = ?`,
      [now, orderId],
    );
  }
}

export async function syncQueueEnqueue(
  operation: string,
  entityType: string,
  entityId: string,
  payloadJson: string,
): Promise<void> {
  const now = new Date().toISOString();
  await db.run(
    `INSERT INTO ${SYNC_QUEUE_TABLE} (operation, entity_type, entity_id, payload_json, status, created_at, updated_at) VALUES (?, ?, ?, ?, 'pending', ?, ?)`,
    [operation, entityType, entityId, payloadJson, now, now],
  );
}

const PROCESSING_STUCK_MINUTES = 5;

export async function syncQueueGetPending(
  limit = 10,
): Promise<SyncQueueEntry[]> {
  const stuckCutoff = new Date(
    Date.now() - PROCESSING_STUCK_MINUTES * 60_000,
  ).toISOString();
  const rows = await db.query<
    SyncQueueEntry & { idempotency_key?: string | null }
  >(
    `SELECT id, operation, entity_type, entity_id, payload_json, status, attempts, max_attempts, error_message, idempotency_key, created_at, processed_at FROM ${SYNC_QUEUE_TABLE} WHERE (status IN ('pending', 'failed') AND attempts < max_attempts) OR (status = 'processing' AND updated_at < ? AND attempts < max_attempts) ORDER BY created_at ASC LIMIT ?`,
    [stuckCutoff, Math.max(1, Math.min(limit, 50))],
  );
  return rows.map((r) => ({
    id: Number(r.id),
    operation: String(r.operation),
    entity_type: String(r.entity_type),
    entity_id: String(r.entity_id),
    payload_json: String(r.payload_json),
    status: String(r.status),
    attempts: Number(r.attempts),
    max_attempts: Number(r.max_attempts),
    error_message: r.error_message != null ? String(r.error_message) : null,
    idempotency_key:
      r.idempotency_key != null ? String(r.idempotency_key) : null,
    created_at: String(r.created_at),
    processed_at: r.processed_at != null ? String(r.processed_at) : null,
  }));
}

export async function syncQueueUpdateEntry(
  id: number,
  status: string,
  errorMessage?: string | null,
  processedAt?: string | null,
  idempotencyKey?: string | null,
): Promise<void> {
  const now = new Date().toISOString();
  const updates: string[] = [
    'status = ?',
    'error_message = ?',
    'updated_at = ?',
  ];
  const values: unknown[] = [status, errorMessage ?? null, now];
  if (processedAt != null) {
    updates.push('processed_at = ?');
    values.push(processedAt);
  }
  if (idempotencyKey !== undefined) {
    updates.push('idempotency_key = ?');
    values.push(idempotencyKey ?? null);
  }
  values.push(id);
  await db.run(
    `UPDATE ${SYNC_QUEUE_TABLE} SET ${updates.join(', ')} WHERE id = ?`,
    values,
  );
}

export async function syncQueueSetIdempotencyKey(
  id: number,
  idempotencyKey: string,
): Promise<void> {
  await db.run(
    `UPDATE ${SYNC_QUEUE_TABLE} SET idempotency_key = ? WHERE id = ?`,
    [idempotencyKey, id],
  );
}

export async function syncQueueIncrementAttempts(id: number): Promise<void> {
  const now = new Date().toISOString();
  await db.run(
    `UPDATE ${SYNC_QUEUE_TABLE} SET attempts = attempts + 1, status = 'processing', updated_at = ? WHERE id = ?`,
    [now, id],
  );
}

export async function syncQueueGetPendingCount(): Promise<number> {
  const rows = await db.query<{ count: number }>(
    `SELECT COUNT(*) as count FROM ${SYNC_QUEUE_TABLE} WHERE status IN ('pending', 'failed') AND attempts < max_attempts`,
  );
  const val = rows[0]?.count;
  return typeof val === 'number' ? val : Math.max(0, Number(val) || 0);
}

export async function closeSqliteStore(): Promise<void> {
  await db.close();
}
